import { zodResolver } from "@hookform/resolvers/zod";
import {
  type ApiResponsePage,
  TextractDocument,
} from "amazon-textract-response-parser";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { SubmitButton as Button } from "~/components/form/submit-button";
import TextBox from "~/components/form/text-box";
// import { createSkeletonSchema } from "~/utils/zod";
// import { getGenericFiles } from "~/services/api.services";
// import TextBox from "~/components/form/text-box";
import {
  getTemplateTableFiles,
  saveTableTemplate,
} from "~/services/supervisor.services";
import type { TableModelData, TableModelFiles } from "~/types/supervisor-types";
import { cn } from "~/utils/tailwind";
import { createSkeletonSchema } from "~/utils/zod";

import { useTemplate } from "~/hooks/use-template";
import {
  templateEditHeaderRoute,
  templateEditTableRoute,
} from "~/pages/all-routes";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "react-toastify";
import CanvasComponent from "./canvas";
import { Tooltip as ReactTooltip } from "react-tooltip";
import LoadingIcon from "~/components/form/LoadingIcon";

export type ContextMenuData = {
  id: number;
  text: string;
  columnIndex?: number;
};

export type SelectedData = {
  tagid?: number;
  columnIndex: number;
  rowIndex?: number;
  table_block_id: string;
  headerText: string;
};

const CropImageModelCollectionSchema = z.object({
  modelno: z.number(),
  tagid: z.number(),
  imageheight: z.number(),
  imagewidth: z.number(),
  x: z.number(),
  y: z.number(),
  match_tag: z.string(),
  ReplaceText: z.string(),
  RegexText: z.string(),
  Id: z.string(),
  hardcodetext: z.string(),
  column_index: z.string(),
});

const IgnoreListSchema = z.object({
  column_index: z.string(),
  column_value: z.string(),
});

const CropImageModelSchema = z.object({
  CropImageModel: z.object({
    fileurl: z.string(),
    hint_1: z.string(),
    hint_2: z.string(),
    hint_3: z.string(),
    hint_4: z.string(),
    hint_5: z.string(),
    ignore_rows_json: z.string(),
    Jsonurl: z.string(),
    match_tag_1: z.string(),
    match_tag_2: z.string(),
    match_tag_3: z.string(),
    match_tag_4: z.string(),
    match_tag_5: z.string(),
    modeldesc: z.string(),
    modelno: z.string().nullable(),
    status: z.number().default(2),
    table_block_id: z.string(),
    ignore_list: z.array(IgnoreListSchema),
    CropImageModelCollection: z.array(CropImageModelCollectionSchema),
  }),
});

type CropImageModelCollection = z.infer<typeof CropImageModelCollectionSchema>;
type CropImageModel = z.infer<typeof CropImageModelSchema>;
type IgnoreList = z.infer<typeof IgnoreListSchema>;

const TemplateEditTableComponent = () => {
  const navigate = useNavigate();
  const { id: templateId } = templateEditTableRoute.useSearch();
  const { updateSelectedField, selectedField } = useTemplate();
  const [alltextractData, setAllTextractData] = useState<TextractDocument[]>(
    []
  );
  const [allImages, setAllImages] = useState<Blob[]>([]);
  const [tableFields, setTableFields] = useState<TableModelFiles>();
  const [templateData, setTemplateData] = useState<TableModelData>();
  // const [fields, setFields] = useState<CropImageModelCollection[]>([]);
  const [enableRowSelection, setEnableRowSelection] = useState<boolean>(false);
  // const triggerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [canvasLoading, setCanvasLoading] = useState(true);

  if (templateId == null) window.history.back();

  useEffect(() => {
    void (async () => {
      async function getTemplateData(templateId: any) {
        const { status, data } = await getTemplateTableFiles(templateId);
        if (status && data != null) {
          console.log(data);
          setTableFields(data);
        }
      }
      if (templateId != null) {
        await getTemplateData(templateId);
      }
    })();
  }, [setTemplateData, templateId]);

  const saveTemplateForm = useForm({
    resolver: zodResolver(CropImageModelSchema),
    defaultValues: {
      CropImageModel: {
        fileurl: "",
        hint_1: "",
        hint_2: "",
        hint_3: "",
        hint_4: "",
        hint_5: "",
        ignore_rows_json: "[]",
        Jsonurl: "",
        match_tag_1: "",
        match_tag_2: "",
        match_tag_3: "",
        match_tag_4: "",
        match_tag_5: "",
        modeldesc: "",
        modelno: "0",
        status: 2,
        table_block_id: "",
        ignore_list: JSON.parse(
          templateData?.ignore_rows_json ? templateData?.ignore_rows_json : "[]"
        ) as IgnoreList[],
        CropImageModelCollection: [] as CropImageModelCollection[],
      },
    },
  });

  const { setValue, getValues, control, trigger } = saveTemplateForm;

  const { fields, update } = useFieldArray({
    control,
    name: "CropImageModel.CropImageModelCollection",
  });

  const {
    update: updateIgnoreList,
    append,
    fields: ignoreFields,
    replace,
    remove,
  } = useFieldArray({
    control,
    name: "CropImageModel.ignore_list",
  });

  const onSubmit = async (data: CropImageModel) => {
    const newData = { ...data };

    newData.CropImageModel.status = 2;
    newData.CropImageModel.ignore_rows_json = JSON.stringify(
      newData.CropImageModel.ignore_list.map((ifd) => {
        console.log(ifd);
        return {
          column_index: ifd.column_index,
          column_value: ifd.column_value,
        } satisfies IgnoreList;
      })
    );

    const { status, data: response } = await saveTableTemplate(newData);
    if (status) {
      console.log(response);
      toast.success("Template Table Saved", {
        theme: "colored",
      });
    } else {
      toast.error("Error! Template Table Not Saved.", {
        theme: "colored",
      });
    }
  };

  // const onSaveSubmit = async (data: CropImageModel) => {
  //   data.CropImageModel.status = 3;
  //   const { status, data: response } = await saveTableTemplate(
  //     data,
  //     templateId.toString()
  //   );
  //   if (status) {
  //     console.log(response);
  //     toast.success("Template Table Save and Submitted", {
  //       theme: "colored",
  //     });
  //   } else {
  //     toast.error("Error! Template Table Not Submitted.", {
  //       theme: "colored",
  //     });
  //   }
  // };

  const handleOnSelect = useCallback(
    (
      {
        tagid,
        columnIndex,
        table_block_id,
        headerText,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        rowIndex,
      }: SelectedData,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tableType: "header" | "table" = "table"
    ) => {
      console.log(
        tagid,
        columnIndex,
        table_block_id,
        headerText,
        enableRowSelection
      );
      if (!enableRowSelection) {
        const fieldIndex = fields.findIndex(
          (field: any) => field.tagid === tagid
        );
        if (fieldIndex !== -1) {
          const updatedField = fields[fieldIndex];
          if (updatedField.column_index != "0") {
            updatedField.column_index = "0";
            updatedField.match_tag = "";
            updatedField.ReplaceText = "";
            updatedField.hardcodetext = "";
          } else {
            updatedField.column_index = columnIndex.toString();
            updatedField.match_tag = headerText;
          }
          update(fieldIndex, updatedField);
          setValue(
            `CropImageModel.CropImageModelCollection.${fieldIndex}`,
            updatedField,
            {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            }
          );
          console.log(table_block_id);
          setValue(`CropImageModel.table_block_id`, table_block_id, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
          void trigger(`CropImageModel.CropImageModelCollection.${fieldIndex}`);
        }
      } else {
        console.log(selectedField, tableType);
        if (tableType === "table" && rowIndex != null) {
          const fieldIndex = ignoreFields.findIndex(
            (_, idx: number) => idx === selectedField?.field
          );
          if (fieldIndex !== -1 && headerText.trim().length > 0) {
            const updatedField = { ...ignoreFields[fieldIndex] };
            updatedField.column_value = headerText;
            updatedField.column_index = columnIndex.toString();
            updateIgnoreList(fieldIndex, updatedField);
            setValue(`CropImageModel.ignore_list.${fieldIndex}`, updatedField);
          }
          setEnableRowSelection(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enableRowSelection,
      fields,
      ignoreFields,
      selectedField,
      setValue,
      trigger,
      update,
      updateIgnoreList,
    ]
  );

  const handleFieldDelete = (tagid: number) => {
    const templateFieldIndex = fields.findIndex(
      (field: CropImageModelCollection) => field.tagid === tagid
    );
    if (templateFieldIndex !== -1) {
      const field: CropImageModelCollection = fields[templateFieldIndex];
      field.column_index = "0";
      field.match_tag = "";
      field.ReplaceText = "";
      field.hardcodetext = "";
      setValue(
        `CropImageModel.CropImageModelCollection.${templateFieldIndex}`,
        field
      );
      const isAllColumnIndexEmpty = fields.every(
        (field: any) => field.column_index === "0"
      );
      if (isAllColumnIndexEmpty) {
        setValue("CropImageModel.table_block_id", "");
      }
      update(templateFieldIndex, field);
      trigger();
    }
  };

  const handleAddIgnoreRow = () => {
    append({
      column_index: "",
      column_value: "",
    } satisfies IgnoreList);
    console.log(ignoreFields);
    setValue(
      `CropImageModel.ignore_rows_json`,
      JSON.stringify(
        ignoreFields.map((ifd) => {
          return {
            column_index: ifd.column_index,
            column_value: ifd.column_value,
          } satisfies IgnoreList;
        })
      )
    );
  };

  const fetchAllJSONTemplates = useCallback(async () => {
    if (tableFields === null || tableFields?.data === null) return [];
    if (templateData != null) {
      const allJSONTemplates =
        process.env.NODE_ENV !== "development"
          ? tableFields?.data?.extractiondocuments.map(async (ed) =>
              fetch(ed.json_presigned_url)
                .then(async (res) => res.json())
                .then((data) => new TextractDocument(data as ApiResponsePage))
            )
          : ["/temp/o-image.json"].map(async (url) =>
              fetch(url)
                .then(async (res) => res.json())
                .then((data) => new TextractDocument(data as ApiResponsePage))
            );
      const allImages =
        process.env.NODE_ENV === "development"
          ? ["/temp/o-image.png"].map((ed) =>
              fetch(ed).then((res) => res.blob())
            )
          : templateData.extractiondocuments.map((ed) =>
              fetch(ed.file_presigned_url).then((res) => res.blob())
            );

      if (!allJSONTemplates || allJSONTemplates?.length === 0) return;
      const [data, images] = await Promise.all([
        Promise.all(allJSONTemplates),
        Promise.all(allImages),
      ]);
      setAllTextractData(data as TextractDocument[]);
      setAllImages(images as Blob[]);
    }
  }, [tableFields, templateData]);

  useEffect(() => {
    if (templateData != null) void fetchAllJSONTemplates();
  }, [fetchAllJSONTemplates, templateData]);

  useEffect(() => {
    if (tableFields !== null && tableFields?.data !== null) {
      setTemplateData(tableFields?.data);
      const values = getValues();
      type TemplateString =
        `CropImageModel.${keyof typeof values.CropImageModel}`;
      for (const key in templateData) {
        if (Object.prototype.hasOwnProperty.call(values.CropImageModel, key)) {
          const valueType =
            CropImageModelSchema.shape.CropImageModel.shape[
              key as keyof typeof values.CropImageModel
            ];
          const value = (tableFields?.data as Record<string, any>)[key];

          if (valueType instanceof z.ZodString) {
            setValue(`CropImageModel.${key}` as TemplateString, value ?? "");
          } else if (valueType instanceof z.ZodNumber) {
            setValue(
              `CropImageModel.${key}` as TemplateString,
              (tableFields?.data as Record<string, any>)[key] as number
            );
          } else if (valueType instanceof z.ZodNullable) {
            setValue(`CropImageModel.${key}` as TemplateString, value + "");
          }
        }
      }

      const tags: CropImageModelCollection[] = (
        tableFields?.data?.tags ?? []
      ).map((tag: any) => {
        const tagObject = createSkeletonSchema(CropImageModelCollectionSchema);

        tagObject.modelno = tableFields?.data?.modelno;
        tagObject.tagid = tag.tagid;
        tagObject.column_index = tag.column_index || "0";
        tagObject.hardcodetext = tag.hardcodetext || "";
        tagObject.ReplaceText = tag.replacetext || "";
        tagObject.match_tag = tag.match_tag || "";
        return tagObject as CropImageModelCollection;
      });
      setValue(`CropImageModel.CropImageModelCollection`, tags);

      const jsonRows = tableFields?.data?.ignore_rows_json;
      const ignoreRows: IgnoreList[] = JSON.parse(
        jsonRows === "" ? "[]" : (tableFields?.data?.ignore_rows_json ?? "[]")
      ).map((row: IgnoreList) => {
        return {
          column_index: row.column_index,
          column_value: row.column_value,
        } satisfies IgnoreList;
      });
      replace(ignoreRows);
    }
  }, [tableFields, getValues, setValue, templateData, replace]);

  return (
    <div>
      {canvasLoading && <LoadingIcon />}
      <FormProvider {...saveTemplateForm}>
        <div className="bg-neutral-100 h-full shadow-md overflow">
          <div className="p-2 h-full">
            <form
              className="h-full"
              onSubmit={saveTemplateForm.handleSubmit(onSubmit)}
            >
              <div className="flex flex-col h-full border-neutral-300 border-2">
                <div className="flex items-center justify-between p-2 border-b-2 border-b-neutral-300">
                  {templateData != null ? (
                    <div className="flex gap-2">
                      <div className="flex gap-2">
                        <div className="font-semibold">Model No:</div>
                        <div>{templateData.modelno}</div>
                      </div>
                      <div className="flex gap-2">
                        <div className="font-semibold">Model Description:</div>
                        <div>{templateData.modeldesc}</div>
                      </div>
                    </div>
                  ) : (
                    <>&nbsp;</>
                  )}
                  <div className="flex gap-2">
                    {/* <Button
                    size="small"
                    type="button"
                    onClick={() => {
                      navigate({
                        to: templateRoute.fullPath,
                      });
                    }}
                  >
                    Back to menu
                  </Button> */}
                    <Button
                      size="small"
                      type="button"
                      onClick={saveTemplateForm.handleSubmit(onSubmit)}
                    >
                      Save
                    </Button>
                    {/* <Button
                    size="small"
                    type="button"
                    onClick={saveTemplateForm.handleSubmit(onSaveSubmit)}
                  >
                    Save & Submit
                  </Button> */}
                    <Button
                      size="small"
                      type="button"
                      onClick={() => {
                        navigate({
                          to: templateEditHeaderRoute.fullPath,
                          search: { id: templateId },
                        });
                      }}
                    >
                      Header Extraction
                    </Button>
                  </div>
                </div>
                <div className="flex h-full overflow-hidden">
                  <div className="flex-grow w-2/6 overflow-y-scroll relative">
                    <table className="table-auto w-full">
                      <thead>
                        <tr>
                          <th className="border border-neutral-300 border-solid">
                            {}
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Tag
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Header
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Column
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Fixed Value
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Exclude Text
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            colSpan={6}
                            className="bg-blue-200 font-semibold border border-neutral-300 border-solid p-2"
                          >
                            Table
                          </td>
                        </tr>
                        {templateData?.tags.map((tag) => (
                          <tr
                            key={tag.tagid}
                            className={cn(
                              tag.tagid === selectedField?.field &&
                                !selectedField.isIgnoreRows &&
                                "bg-yellow-200"
                            )}
                            onClick={() => {
                              updateSelectedField({
                                field: tag.tagid,
                                isIgnoreRows: false,
                              });
                            }}
                          >
                            <td className="border border-neutral-300 border-solid p-1 w-12">
                              {fields.find(
                                (field: CropImageModelCollection) =>
                                  field.tagid === tag.tagid
                              )?.column_index !== "0" && (
                                <Button
                                  size={"small"}
                                  variant={"transparent"}
                                  onClick={() => {
                                    handleFieldDelete(tag.tagid);
                                  }}
                                  type="button"
                                  className="text-red-500 font-bold"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                    className="size-5"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M6 18 18 6M6 6l12 12"
                                    />
                                  </svg>
                                </Button>
                              )}
                            </td>
                            <td className="text-center border border-neutral-300 border-solid p-2 cursor-pointer">
                              {tag.displayname}
                            </td>
                            <td className="text-center border border-neutral-300 border-solid p-2 relative">
                              <TextBox
                                name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                  (f) => f.tagid === tag.tagid
                                )}.match_tag`}
                                data-tooltip-content={
                                  fields[
                                    fields.findIndex(
                                      (f) => f.tagid === tag.tagid
                                    )
                                  ]?.match_tag || "No data available"
                                }
                                data-tooltip-id={"tooltip-${tag.tagid}"}
                              />
                              <ReactTooltip id={"tooltip-${tag.tagid}"} />
                            </td>
                            <td className="text-center border border-neutral-300 border-solid p-2 relative">
                              <TextBox
                                name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                  (f) => f.tagid === tag.tagid
                                )}.column_index`}
                              />
                            </td>
                            <td className="text-center border border-neutral-300 border-solid p-2">
                              <TextBox
                                name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                  (f) => f.tagid === tag.tagid
                                )}.hardcodetext`}
                              />
                            </td>
                            <td className="text-center border border-neutral-300 border-solid p-2">
                              <TextBox
                                name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                  (f) => f.tagid === tag.tagid
                                )}.ReplaceText`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <table className="table-auto w-full">
                      <thead>
                        <tr>
                          <td
                            colSpan={6}
                            className="bg-blue-200 font-semibold border border-neutral-300 border-solid p-2"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span>Ignore Rows</span>
                              <Button
                                type="button"
                                size={"small"}
                                onClick={handleAddIgnoreRow}
                              >
                                +
                              </Button>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th
                            colSpan={2}
                            className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid"
                          >
                            Column Number
                          </th>
                          <th
                            colSpan={2}
                            className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid"
                          >
                            Value
                          </th>
                          <th
                            colSpan={2}
                            className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid"
                          >
                            Remove Row
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ignoreFields.map((row, idx: number) => (
                          <tr
                            key={row.id}
                            onClick={() => {
                              setEnableRowSelection(true);
                              updateSelectedField({
                                field: idx,
                                isIgnoreRows: true,
                              });
                            }}
                            className={cn(
                              idx === selectedField?.field && "bg-yellow-200"
                            )}
                          >
                            <td
                              colSpan={2}
                              className="border border-neutral-300 border-solid p-1 w-20"
                            >
                              <TextBox
                                name={`CropImageModel.ignore_list.${idx}.column_index`}
                                showLabel={false}
                                onClick={() => {
                                  // setEnableRowSelection(true);
                                }}
                              />
                            </td>
                            <td
                              className="border border-neutral-300 border-solid p-1"
                              colSpan={2}
                            >
                              <TextBox
                                name={`CropImageModel.ignore_list.${idx}.column_value`}
                                showLabel={false}
                                onClick={() => {
                                  // setEnableRowSelection(true);
                                }}
                              />
                            </td>
                            <td
                              colSpan={2}
                              className="border border-neutral-300 border-solid p-1 text-center"
                            >
                              <Button
                                size={"small"}
                                onClick={() => {
                                  remove(idx);
                                }}
                              >
                                -
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="w-4/6 overflow-x-hidden overflow-y-scroll">
                    {alltextractData.length > 0 && templateData != null && (
                      <CanvasComponent
                        imageUrls={allImages.map((image) =>
                          URL.createObjectURL(image)
                        )}
                        table_block_id={getValues(
                          "CropImageModel.table_block_id"
                        )}
                        textractAllData={alltextractData}
                        activeColumns={fields
                          .filter((field) => field.column_index !== "0")
                          .map(
                            (field) =>
                              ({
                                id: field.tagid,
                                text: "",
                                columnIndex: +field.column_index,
                              }) satisfies ContextMenuData
                          )}
                        contextMenuData={templateData?.tags.map(
                          (tag) =>
                            ({
                              id: tag.tagid,
                              text: tag.displayname,
                              columnIndex: +tag.column_index,
                            }) satisfies ContextMenuData
                        )}
                        enableRowSelection={enableRowSelection}
                        onSelect={handleOnSelect}
                        setCanvasLoading={setCanvasLoading}
                      />
                    )}
                  </div>
                  <div></div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default TemplateEditTableComponent;
